// src/components/Admin/AdminEmails.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Chip,
  Divider,
  Tabs,
  Tab,
  MenuItem,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  Tooltip,
  TableRow,
  TableContainer,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SelectEstado from "../common/SelectEstado";
import RichTextEditor from "../Utiles/RichTextEditor";

// Sanitiza por si viene con "/"
const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

/* ===========================
   Utils / helpers
=========================== */
const schema = Yup.object({
  asunto: Yup.string().required("El título/asunto es obligatorio"),
  cuerpo: Yup.string().nullable(),
  firma: Yup.string().nullable(),
});

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const valido = (e) => emailRegex.test((e || "").trim().toLowerCase());

// Lee camel o snake indistintamente
const get = (obj, camel, snake) => obj?.[camel] ?? obj?.[snake];

const FECHA = (row) =>
  (get(row, "fechaEnvio", "fecha_envio") || "").replace("T", " ").slice(0, 19);
const LANZADO_POR = (row) => get(row, "lanzadoPor", "lanzado_por");
const TOTAL_DEST = (row) => get(row, "totalDestinatarios", "total_destinatarios");
const DESTINATARIO = (row) => {
  const destinatarios =
    get(row, "destinatarios", "destinatarios") ||
    get(row, "emails", "emails") ||
    get(row, "destinatario", "destinatario");

  if (Array.isArray(destinatarios)) {
    if (destinatarios.length === 0) return "-";

    const primero = destinatarios[0];
    return destinatarios.length > 1
      ? `${primero} y ${destinatarios.length - 1} más`
      : primero;
  }

  if (typeof destinatarios === "string" && destinatarios.trim()) {
    const emails = extraerEmails(destinatarios);

    if (!emails.length) return destinatarios;

    return emails.length > 1
      ? `${emails[0]} y ${emails.length - 1} más`
      : emails[0];
  }

  return "-";
};

// Extrae emails de cualquier texto (excel, CSV, etc.)
function extraerEmails(raw = "") {
  const text = String(raw || "").trim();
  if (!text) return [];

  // Normaliza separadores típicos de Excel/CSV
  // - salto de línea \n
  // - tabulador \t (cuando copias varias columnas)
  // - ; , y espacios
  const tokens = text
    .replace(/\r/g, "\n")
    .split(/[\n\t;,\s]+/g)
    .map((t) => t.trim())
    .filter(Boolean);

  // Limpia y filtra válidos
  const out = [];
  const seen = new Set();
  for (const t of tokens) {
    const e = t.toLowerCase();
    if (valido(e) && !seen.has(e)) {
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

const DestinatariosChips = ({ chips, setChips }) => {
  const [input, setInput] = useState("");
  const [lastInfo, setLastInfo] = useState(null); // info opcional tipo "añadidos X / inválidos Y"

  const addMany = (raw) => {
    const emails = extraerEmails(raw);

    if (!emails.length) {
      setLastInfo({ added: 0, invalid: raw?.trim() ? raw.trim() : "" });
      return;
    }

    setChips((prev) => {
      const prevSet = new Set(prev.map((x) => x.toLowerCase()));
      const nuevos = [];
      for (const e of emails) {
        if (!prevSet.has(e)) {
          prevSet.add(e);
          nuevos.push(e);
        }
      }
      setLastInfo({ added: nuevos.length, invalid: null });
      return [...prev, ...nuevos];
    });
  };

  const addChip = () => {
    const raw = input.trim();
    if (!raw) return;

    // Si el usuario mete varios (o pega)
    const emails = extraerEmails(raw);
    if (emails.length > 1 || raw.includes("\n") || raw.includes("\t") || raw.includes(";") || raw.includes(",") || raw.includes(" ")) {
      addMany(raw);
      setInput("");
      return;
    }

    // Caso 1 email
    const e = raw.toLowerCase();
    if (valido(e) && !chips.map((c) => c.toLowerCase()).includes(e)) {
      setChips((prev) => [...prev, e]);
      setLastInfo({ added: 1, invalid: null });
    } else {
      setLastInfo({ added: 0, invalid: raw });
    }
    setInput("");
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;

    // Si parece pegado masivo (excel/varios), lo procesamos directamente
    const looksBulk =
      text.includes("\n") || text.includes("\t") || text.includes(";") || text.includes(",") || text.split(" ").length > 2;

    if (looksBulk) {
      e.preventDefault();
      addMany(text);
      setInput("");
    }
  };

  const limpiar = () => {
    setChips([]);
    setInput("");
    setLastInfo(null);
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
        <TextField
          label="Añadir correo(s) (puedes pegar desde Excel)"
          size="small"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPaste={onPaste}
          placeholder="Ej: a@a.com; b@b.com  (o pega una columna de Excel)"
          fullWidth
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={addChip} disabled={!input.trim()}>
            Añadir
          </Button>
          <Button variant="text" color="error" onClick={limpiar} disabled={chips.length === 0}>
            Vaciar
          </Button>
        </Stack>
      </Stack>

      {lastInfo?.added ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          ✔ Añadidos: <b>{lastInfo.added}</b>
        </Typography>
      ) : lastInfo?.invalid ? (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
          ⚠ No se pudo añadir: <b>{String(lastInfo.invalid).slice(0, 80)}</b>
        </Typography>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
        {chips.map((c) => (
          <Chip
            key={c}
            label={c}
            onDelete={() => setChips((p) => p.filter((x) => x !== c))}
          />
        ))}
      </Stack>
    </Box>
  );
};

/* ===========================
   TAB 1: Envío masivo
=========================== */
function TabEnvioMasivo() {
  const token = localStorage.getItem("token");
  const [chips, setChips] = useState([]);
  const [files, setFiles] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [preview, setPreview] = useState(null);
  const [snack, setSnack] = useState({ open: false, sev: "success", msg: "" });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const axiosCfg = useMemo(() => ({ headers /*, withCredentials:true*/ }), [headers]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/email/plantillas`, axiosCfg);
        setPlantillas(data || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [axiosCfg]);

  const onPickFiles = (evt) => {
    const picked = Array.from(evt.target.files || []);
    if (!picked.length) return;
    setFiles((prev) => {
      const map = new Map(prev.map((f) => [`${f.name}:${f.size}:${f.lastModified}`, f]));
      for (const f of picked) map.set(`${f.name}:${f.size}:${f.lastModified}`, f);
      return Array.from(map.values());
    });
    evt.target.value = null; // permitir volver a elegir el mismo archivo
  };

  const errMsg = (e) =>
    e?.response?.data?.mensaje ||
    e?.response?.data?.message ||
    (typeof e?.response?.data === "string" ? e.response.data : "") ||
    e?.message ||
    "Error desconocido";

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Correos masivos
      </Typography>
      <Formik
        initialValues={{
          asunto: "",
          cuerpo: "",
          firma: "Colegio de Enfermería de Valladolid",
          incluirTodos: false,
          estadoId: "",
          plantillaId: "",
        }}
        validationSchema={schema}
        onSubmit={async (values) => {
          try {
            setSending(true);
            const payload = {
              asunto: values.asunto,
              cuerpo: values.cuerpo || undefined,
              firma: values.firma || undefined,
              incluir_todos: values.incluirTodos || undefined,
              estado_id: values.estadoId === "" ? undefined : Number(values.estadoId),
              destinatarios: chips.length ? chips : undefined,
              plantilla_id: values.plantillaId || undefined,
            };

            const fd = new FormData();
            fd.append("payload", new Blob([JSON.stringify(payload)], { type: "application/json" }));
            for (const f of files) fd.append("adjuntos", f);

            const { data, status } = await axios.post(`${API}/email/enviar-multipart`, fd, axiosCfg);

            if (status === 202) {
              const id = data.envio_id ?? data.envioId ?? "-";
              setSnack({
                open: true,
                sev: "success",
                msg: `⏳ Encolado. Envío #${id}. Total: ${data.total}`,
              });
            } else {
              setSnack({
                open: true,
                sev: "success",
                msg: `✔ Enviado. Destinatarios: ${data.total} | OK: ${data.enviados} | KO: ${data.fallidos}`,
              });
            }
            setPreview(null);
          } catch (e) {
            console.error(e);
            setSnack({ open: true, sev: "error", msg: `Error al enviar: ${errMsg(e)}` });
          } finally {
            setSending(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => {
          const onPlantillaChange = (e) => {
            const id = e.target.value;
            setFieldValue("plantillaId", id);
            const p = plantillas.find((pp) => String(pp.id) === String(id));
            if (p) {
              setFieldValue("asunto", p.asunto || "");
              setFieldValue("cuerpo", p.cuerpo || "");
              setFieldValue("firma", p.firma || "Colegio de Enfermería de Valladolid");
            }
          };

          return (
            <Form>
              <Stack spacing={2}>
                <TextField
                  label="Título / Asunto"
                  name="asunto"
                  value={values.asunto}
                  onChange={handleChange}
                  error={touched.asunto && Boolean(errors.asunto)}
                  helperText={touched.asunto && errors.asunto}
                  fullWidth
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    label="Plantilla"
                    name="plantillaId"
                    value={values.plantillaId}
                    onChange={onPlantillaChange}
                    fullWidth
                  >
                    <MenuItem value="">(Sin plantilla)</MenuItem>
                    {plantillas.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.nombre}
                      </MenuItem>
                    ))}
                  </TextField>

                  <FormControlLabel
                    control={
                      <Checkbox
                        name="incluirTodos"
                        checked={values.incluirTodos}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.checked) setFieldValue("estadoId", ""); // desactiva estado
                        }}
                      />
                    }
                    label="Incluir a todos los colegiados"
                  />
                </Stack>

                <SelectEstado
                  label="Filtrar por estado (opcional)"
                  name="estadoId"
                  value={values.estadoId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFieldValue("estadoId", v === "" ? "" : Number(v));
                    setFieldValue("incluirTodos", false);
                  }}
                  fullWidth
                  allowEmpty
                  disabled={values.incluirTodos}
                />

                <RichTextEditor
                  value={values.cuerpo}
                  onChange={(html) => setFieldValue("cuerpo", html)}
                  placeholder="Escribe el contenido del correo…"
                />

                <TextField label="Firma" name="firma" value={values.firma} onChange={handleChange} fullWidth />

                <Divider />

                <DestinatariosChips chips={chips} setChips={setChips} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button component="label" variant="outlined">
                    Adjuntar archivos
                    <input hidden type="file" multiple onChange={onPickFiles} />
                  </Button>
                  <Button type="button" onClick={() => setFiles([])} disabled={files.length === 0}>
                    Vaciar adjuntos
                  </Button>
                </Stack>

                {files.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    {files.map((f, i) => (
                      <Chip
                        key={`${f.name}-${f.size}-${f.lastModified}-${i}`}
                        label={f.name}
                        onDelete={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      />
                    ))}
                  </Stack>
                )}

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    type="button"
                    variant="text"
                    onClick={async () => {
                      if (!values.asunto?.trim()) {
                        setSnack({
                          open: true,
                          sev: "warning",
                          msg: "Escribe un asunto antes de previsualizar",
                        });
                        return;
                      }
                      try {
                        setLoadingPreview(true);
                        const payload = {
                          asunto: values.asunto,
                          cuerpo: values.cuerpo || undefined,
                          firma: values.firma || undefined,
                          incluir_todos: values.incluirTodos || undefined,
                          estado_id: values.estadoId === "" ? undefined : Number(values.estadoId),
                          destinatarios: chips.length ? chips : undefined,
                          plantilla_id: values.plantillaId || undefined,
                        };
                        const { data } = await axios.post(`${API}/email/preview`, payload, axiosCfg);
                        setPreview(data);
                      } catch (e) {
                        console.error(e);
                        setPreview(null);
                        setSnack({ open: true, sev: "error", msg: `No se pudo previsualizar: ${e?.message || "error"}` });
                      } finally {
                        setLoadingPreview(false);
                      }
                    }}
                  >
                    Previsualizar destinatarios
                  </Button>
                  {loadingPreview && <CircularProgress size={20} />}
                  {preview && (
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Total: <b>{preview.total}</b>
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button type="submit" variant="contained" disabled={sending}>
                    {sending ? "Enviando..." : "Enviar"}
                  </Button>
                </Stack>

                {preview && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2">Muestra (primeros 10)</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                      {preview.muestra?.map((e) => <Chip key={e} label={e} />)}
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Form>
          );
        }}
      </Formik>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.sev} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

function DestinatariosHistorial({ row, axiosCfg }) {
  const [emails, setEmails] = useState(null);
  const [loading, setLoading] = useState(false);

  const total = TOTAL_DEST(row);
  const primero = row.primer_destinatario;

  if (!primero) {
    return <span>-</span>;
  }

  if (total <= 1) {
    return <span>{primero}</span>;
  }

  const cargarDestinatarios = async () => {
    if (emails !== null || loading) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `${API}/email/historial/${row.id}/destinatarios`,
        axiosCfg
      );

      setEmails(data || []);
    } catch (e) {
      console.error(e);
      setEmails([]);
    } finally {
      setLoading(false);
    }

  };

  return (
    <Box>
      <Typography component="span">
        {primero}{" "}
      </Typography>

      <Tooltip
        title={
          loading ? (
            "Cargando destinatarios..."
          ) : emails ? (
            <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
              {emails.map((email, index) => (
                <div key={`${email}-${index}`}>
                  {email}
                </div>
              ))}
            </Box>
          ) : (
            "Cargando destinatarios..."
          )
        }
        onOpen={cargarDestinatarios}
        arrow
      >
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          y {total - 1} más
        </Typography>
      </Tooltip>
    </Box>

  );
}

/* ===========================
   TAB 2: Historial (solo email_envio)
=========================== */
function TabHistorial() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const axiosCfg = useMemo(() => ({ headers /*, withCredentials:true*/ }), [headers]);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [data, setData] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email/historial?page=${page}&size=${size}`, axiosCfg);
      setData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar(); // eslint-disable-next-line
  }, [page, size]);

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6">Historial de envíos</Typography>
        {loading && <CircularProgress size={20} />}
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          label="Tamaño"
          value={size}
          onChange={(e) => {
            setPage(0);
            setSize(Number(e.target.value));
          }}
          sx={{ width: 120 }}
        >
          {[10, 25, 50, 100].map((n) => (
            <MenuItem key={n} value={n}>
              {n}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={cargar}>
          Refrescar
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Asunto</TableCell>
              <TableCell>Enviado por</TableCell>
              <TableCell>Destinatario</TableCell>
              <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.content?.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.id}</TableCell>
                <TableCell>{FECHA(row)}</TableCell>
                <TableCell>{row.asunto}</TableCell>
                <TableCell>{LANZADO_POR(row)}</TableCell>
                <TableCell>
                  <DestinatariosHistorial
                    row={row}
                    axiosCfg={axiosCfg}
                  />
                </TableCell>

                <TableCell align="right">
                  {TOTAL_DEST(row)}
                </TableCell>
                <TableCell align="right">{TOTAL_DEST(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button disabled={page <= 0} onClick={() => setPage((p) => p - 1)}>
          Anterior
        </Button>
        <Typography variant="body2">
          Página {page + 1} / {Math.max(1, data.totalPages)}
        </Typography>
        <Button disabled={page + 1 >= data.totalPages} onClick={() => setPage((p) => p + 1)}>
          Siguiente
        </Button>
      </Stack>
    </Paper>
  );
}

/* ===========================
   TAB 3: Plantillas
=========================== */
function TabPlantillas() {
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const axiosCfg = useMemo(() => ({ headers /*, withCredentials:true*/ }), [headers]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState({
    open: false,
    editId: null,
    form: { nombre: "", asunto: "", cuerpo: "", firma: "" },
  });
  const [snack, setSnack] = useState({ open: false, sev: "success", msg: "" });

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/email/plantillas`, axiosCfg);
      setRows(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []); // eslint-disable-line

  const guardar = async () => {
    try {
      const body = dlg.form;
      if (dlg.editId) {
        await axios.put(`${API}/email/plantillas/${dlg.editId}`, body, axiosCfg);
      } else {
        await axios.post(`${API}/email/plantillas`, body, axiosCfg);
      }
      setSnack({ open: true, sev: "success", msg: "Guardado" });
      setDlg({ open: false, editId: null, form: { nombre: "", asunto: "", cuerpo: "", firma: "" } });
      cargar();
    } catch (e) {
      console.error(e);
      setSnack({ open: true, sev: "error", msg: "Error al guardar" });
    }
  };

  const borrar = async (id) => {
    if (!window.confirm("¿Seguro que deseas eliminar la plantilla?")) return;
    try {
      await axios.delete(`${API}/email/plantillas/${id}`, axiosCfg);
      cargar();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6">Plantillas</Typography>
        {loading && <CircularProgress size={20} />}
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={() =>
            setDlg({ open: true, editId: null, form: { nombre: "", asunto: "", cuerpo: "", firma: "" } })
          }
        >
          Nueva plantilla
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Asunto</TableCell>
              <TableCell>Firma</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.nombre}</TableCell>
                <TableCell>{r.asunto}</TableCell>
                <TableCell>{r.firma || "-"}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    onClick={() =>
                      setDlg({
                        open: true,
                        editId: r.id,
                        form: { nombre: r.nombre, asunto: r.asunto, cuerpo: r.cuerpo, firma: r.firma || "" },
                      })
                    }
                  >
                    Editar
                  </Button>
                  <Button size="small" color="error" onClick={() => borrar(r.id)}>
                    Eliminar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dlg.open}
        onClose={() => setDlg({ open: false, editId: null, form: { nombre: "", asunto: "", cuerpo: "", firma: "" } })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{dlg.editId ? "Editar plantilla" : "Nueva plantilla"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              value={dlg.form.nombre}
              onChange={(e) => setDlg((d) => ({ ...d, form: { ...d.form, nombre: e.target.value } }))}
              fullWidth
            />
            <TextField
              label="Asunto"
              value={dlg.form.asunto}
              onChange={(e) => setDlg((d) => ({ ...d, form: { ...d.form, asunto: e.target.value } }))}
              fullWidth
            />
            <TextField
              label="Firma (opcional)"
              value={dlg.form.firma}
              onChange={(e) => setDlg((d) => ({ ...d, form: { ...d.form, firma: e.target.value } }))}
              fullWidth
            />
            <RichTextEditor
              value={dlg.form.cuerpo}
              onChange={(html) => setDlg((d) => ({ ...d, form: { ...d.form, cuerpo: html } }))}
              placeholder="Cuerpo de la plantilla…"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDlg({ open: false, editId: null, form: { nombre: "", asunto: "", cuerpo: "", firma: "" } })
            }
          >
            Cancelar
          </Button>
          <Button variant="contained" onClick={guardar}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.sev} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

/* ===========================
   Página contenedora
=========================== */
export default function AdminEmails() {
  const [tab, setTab] = useState(0);
  return (
    <Box sx={{ p: 2 }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Enviar" />
        <Tab label="Historial" />
        <Tab label="Plantillas" />
      </Tabs>

      {tab === 0 && <TabEnvioMasivo />}
      {tab === 1 && <TabHistorial />}
      {tab === 2 && <TabPlantillas />}
    </Box>
  );
}
